/**
 * Surgery catalog API (B2 / S4–S5) — surgery/operation/delivery masters with
 * PACKAGE or ITEMIZED pricing.
 *
 * Backend endpoints (org from session; behind B1's `require_admission_enabled`
 * gate + `MANAGE_BED_CONFIG`):
 *
 *   POST   /api/organisation/surgeries
 *   GET    /api/organisation/surgeries?include_inactive=
 *   GET    /api/organisation/surgeries/:surgery_id
 *   PATCH  /api/organisation/surgeries/:surgery_id
 *   DELETE /api/organisation/surgeries/:surgery_id   (soft delete)
 *
 * Clone of bedCategoryApi.js (org-global, no dept scoping). Every function
 * returns `{ success, data?, error?, code? }` — `code` carries the server's
 * machine-readable business-rule code from a `{code, message}` dict detail
 * (PACKAGE_PRICE_REQUIRED, ITEMIZED_NEEDS_COMPONENT, ADMISSION_DISABLED).
 */

import apiClient from './apiClient';

const BASE = '/api/organisation/surgeries';

/** Local clone of the catalog error translator (see bedCategoryApi.js). */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    return { error: detail.message || fallback, code: detail.code };
  }
  if (typeof detail === 'string') return { error: detail };

  switch (error?.response?.status) {
    case 400: return { error: 'Invalid data — please check the form and try again.' };
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to perform this action.' };
    case 404: return { error: 'The requested item could not be found.' };
    case 409: return { error: 'That item already exists. Pick a different name.' };
    case 422: return { error: 'Invalid data — please check the form and try again.' };
    case 500: return { error: 'Server error. Please try again in a moment.' };
    default:  return { error: fallback };
  }
}

/** POST /api/organisation/surgeries → 201 SurgeryResponse */
export const createSurgery = async (body) => {
  try {
    const response = await apiClient.post(BASE, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[SurgeryAPI] create error:', error);
    return { success: false, ...parseError(error, 'Failed to create surgery.') };
  }
};

/**
 * GET /api/organisation/surgeries → {surgeries:[...], count}
 * First GET on an IPD-ON org lazily seeds the presets server-side.
 */
// C1 / S9: the planned-interventions surgery picker anchors on the ADMISSION's
// department. A falsy `department_id` OMITS the param → full catalog
// (rule-(a) never-exclude, fail-open); a set anchor → master_scope union.
export const listSurgeries = async ({ include_inactive, department_id } = {}) => {
  try {
    const params = {};
    if (include_inactive) params.include_inactive = true;
    if (department_id) params.department_id = department_id;
    const response = await apiClient.get(BASE, { params });
    return {
      success: true,
      data: {
        surgeries: response.data?.surgeries || [],
        count: response.data?.count ?? (response.data?.surgeries?.length || 0),
      },
    };
  } catch (error) {
    console.error('[SurgeryAPI] list error:', error);
    return { success: false, ...parseError(error, 'Failed to load surgeries.') };
  }
};

/** GET /api/organisation/surgeries/:id */
export const getSurgery = async (surgery_id) => {
  try {
    const response = await apiClient.get(`${BASE}/${encodeURIComponent(surgery_id)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[SurgeryAPI] get error:', error);
    return { success: false, ...parseError(error, 'Failed to load surgery.') };
  }
};

/** PATCH /api/organisation/surgeries/:id — partial update. */
export const updateSurgery = async (surgery_id, body) => {
  try {
    const response = await apiClient.patch(`${BASE}/${encodeURIComponent(surgery_id)}`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[SurgeryAPI] update error:', error);
    return { success: false, ...parseError(error, 'Failed to update surgery.') };
  }
};

/**
 * B5 / S1 — tag a surgery with 0..N departments ("Visible to"). Empty array
 * clears all edges → owner_scope reverts to ORG_WIDE; ≥1 →
 * owner_scope=DEPARTMENT. Server rejects (422 `{code, message}`) an unknown
 * (DEPARTMENT_NOT_FOUND) / inactive (DEPARTMENT_INACTIVE) / racing
 * (CONCURRENT_MODIFICATION) department. Response = enriched SurgeryResponse.
 * The free-text `specialty` label is orthogonal — this touches only the tag.
 *
 * PUT /api/organisation/surgeries/:id/departments
 * @param {string}   surgery_id
 * @param {string[]} department_ids
 * @returns {Promise<{success, data?, error?, code?}>}
 */
export const setSurgeryDepartments = async (surgery_id, department_ids) => {
  try {
    const response = await apiClient.put(
      `${BASE}/${encodeURIComponent(surgery_id)}/departments`,
      { department_ids: department_ids || [] }
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[SurgeryAPI] setDepartments error:', error);
    return { success: false, ...parseError(error, 'Failed to save departments.') };
  }
};

/** DELETE /api/organisation/surgeries/:id → 200 (soft-delete, active=false) */
export const deactivateSurgery = async (surgery_id) => {
  try {
    const response = await apiClient.delete(`${BASE}/${encodeURIComponent(surgery_id)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[SurgeryAPI] deactivate error:', error);
    return { success: false, ...parseError(error, 'Failed to deactivate surgery.') };
  }
};
